import { useFormikContext } from 'formik'
import { BasicDatePicker } from '../BasicDatePicker'
import dayjs, { Dayjs } from 'dayjs'
import React, { useCallback, useMemo, useState } from 'react'
import { DatePickerProps, DateValidationError } from '@mui/x-date-pickers'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

interface Props<T> extends Omit<DatePickerProps<Dayjs>, 'name' | 'value' | 'onChange'> {
  name: keyof T
  label: string
  minDateError?: string
  maxDateError?: string
}

export const FormikDatePicker = <T extends unknown>({
  name,
  label,
  minDateError,
  maxDateError,
  ...props
}: Props<T>) => {
  const { values, touched, errors, setFieldValue } = useFormikContext<T>()

  const [validationError, setError] = useState<DateValidationError | null>(null)

  const isTouched = touched[name] as boolean
  const error = errors[name] as string | undefined

  const onChange = useCallback(
    (date: Dayjs | null) => {
      const now = dayjs()

      setFieldValue(
        name as string,
        date?.isValid()
          ? date
              .hour(now.hour())
              .minute(now.minute())
              .second(now.second())
              .millisecond(now.millisecond())
              .toISOString()
          : null,
      )
    },
    [name, setFieldValue],
  )
  const errorMessage = useMemo(() => {
    switch (validationError) {
      case 'minDate': {
        return minDateError
      }

      case 'maxDate': {
        return maxDateError
      }

      case 'invalidDate': {
        return 'Your date is not valid'
      }

      default: {
        return ''
      }
    }
  }, [maxDateError, minDateError, validationError])

  const value = useMemo(
    () => (values[name] ? dayjs(values[name] as string).utc() : undefined),
    [values, name],
  )

  const helperText = errorMessage || (isTouched && error)
  const hasError = Boolean(isTouched && error) || Boolean(errorMessage)

  return (
    <BasicDatePicker
      {...props}
      slotProps={{
        ...props.slotProps,
        textField: {
          ...props.slotProps?.textField,
          helperText,
          error: hasError,
        },
      }}
      name={name as string}
      value={value}
      error={hasError}
      label={label}
      onChange={onChange}
      onError={(newError) => setError(newError)}
    />
  )
}
